import React from 'react';
import { IconButton, Paper, Tooltip, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import EvolutionPrescriptionActions from './EvolutionPrescriptionActions';
import type { EvolutionAttachedPrintable, EvolutionPrintableCategory } from './evolutionWorkflowTypes';
import { evolutionPrintableListLabel, isMemedLinkedPrintable } from './evolutionViewHelpers';
import { canEditEvolutionPrintable, evolutionPrintableEditBlockedReason } from './evolutionPrintableEditPolicy';

export interface EvolutionAttachedPrintableChipProps {
  printable: EvolutionAttachedPrintable;
  categoryLabels: Record<EvolutionPrintableCategory, string>;
  disabled?: boolean;
  patientId?: string;
  onEdit: (printable: EvolutionAttachedPrintable) => void;
  onPrint: (printable: EvolutionAttachedPrintable) => void;
  onRemove: (printable: EvolutionAttachedPrintable) => void;
  onPrescriptionSigned?: (info: any) => void;
  onPrescriptionError?: (message: string) => void;
  /** Estilo visual (evolução embutida vs página Evoluções). */
  variant?: 'evolution' | 'clinic';
}

const chipSx = {
  evolution: {
    borderColor: '#e2e8f0',
    bgcolor: '#fff',
  },
  clinic: {
    borderColor: '#e2e8f0',
    bgcolor: '#fff',
  },
};

const EvolutionAttachedPrintableChip: React.FC<EvolutionAttachedPrintableChipProps> = ({
  printable: p,
  categoryLabels,
  disabled = false,
  patientId,
  onEdit,
  onPrint,
  onRemove,
  onPrescriptionSigned,
  onPrescriptionError,
  variant = 'clinic',
}) => {
  const editable = canEditEvolutionPrintable(p);
  const editBlockedReason = evolutionPrintableEditBlockedReason(p);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        pl: 1.25,
        pr: 0.25,
        py: 0.25,
        borderRadius: 10,
        ...chipSx[variant],
      }}
    >
      <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 160, sm: 240 }, fontWeight: 600 }}>
        {p.category === 'prescription' ? evolutionPrintableListLabel(p) : p.templateName}
      </Typography>
      {p.category !== 'prescription' ? (
        <Typography
          variant="caption"
          sx={{ color: '#94a3b8', display: { xs: 'none', sm: 'inline' }, mr: 0.5 }}
        >
          {categoryLabels[p.category]}
        </Typography>
      ) : null}
      {!disabled && (
        <Tooltip title={editable ? 'Editar conteúdo' : editBlockedReason}>
          <span>
            <IconButton
              size="small"
              disabled={!editable}
              onClick={() => onEdit(p)}
              aria-label="Editar"
              sx={{ color: '#3b82f6' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {p.category === 'prescription' && p.linkedDocumentId ? (
        <EvolutionPrescriptionActions
          prescriptionId={p.linkedDocumentId}
          patientId={patientId}
          isMemed={isMemedLinkedPrintable(p)}
          onError={onPrescriptionError ?? (() => {})}
          onSigned={onPrescriptionSigned}
        />
      ) : (
        <Tooltip title="Imprimir este documento">
          <IconButton size="small" onClick={() => onPrint(p)} aria-label="Imprimir">
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!disabled && (
        <Tooltip title="Remover">
          <IconButton
            size="small"
            onClick={() => onRemove(p)}
            aria-label="Remover"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Paper>
  );
};

export default EvolutionAttachedPrintableChip;
